import { Fragment } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import MessageAttachments from "./MessageAttachments";
import MessageReactions from "./MessageReactions";
import {
  formatDateLabel,
  formatDateTimeLabel,
  getDateKey,
  getMessagePreview,
  getMessageTimestampValue,
} from "../messages-utils";

type Props = {
  message: any;
  previousMessage: any | null;
  isOutgoing: boolean;
  highlightedMessageId: string | null;
  reactionPickerForId: string | null;
  onReply: (message: any) => void;
  onToggleReactionPicker: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
  onScrollToReply: (targetId?: string | null) => void;
  onImagePreview: (preview: { src: string; title?: string }) => void;
  resolveParticipantName: (
    senderId?: string | null,
    senderRole?: string | null
  ) => string;
  currentUserId?: string;
  role?: string;
};

const MessageRow = ({
  message,
  previousMessage,
  isOutgoing,
  highlightedMessageId,
  reactionPickerForId,
  onReply,
  onToggleReactionPicker,
  onToggleReaction,
  onScrollToReply,
  onImagePreview,
  resolveParticipantName,
  currentUserId,
  role,
}: Props) => {
  const messageId = message?._id?.toString?.() || message?._id || "";
  const attachments = Array.isArray(message?.attachments)
    ? message.attachments
    : [];
  const messageText = message?.body || "";
  const replyTo = message?.replyTo || null;
  const replyName = replyTo
    ? resolveParticipantName(replyTo?.senderId, replyTo?.senderRole)
    : "";
  const replyPreview = replyTo ? getMessagePreview(replyTo) : "";
  const reactions = Array.isArray(message?.reactions) ? message.reactions : [];
  const messageTimestamp = getMessageTimestampValue(message);
  const dateKey = getDateKey(messageTimestamp);
  const previousDateKey = previousMessage
    ? getDateKey(getMessageTimestampValue(previousMessage))
    : "";
  const showDateSeparator = dateKey && dateKey !== previousDateKey;

  return (
    <Fragment>
      {showDateSeparator && (
        <Box className="message-date-separator">
          <span>{formatDateLabel(messageTimestamp)}</span>
        </Box>
      )}
      <Box
        id={`message-${messageId}`}
        className={`message-row ${isOutgoing ? "outgoing" : "incoming"}${
          messageId === highlightedMessageId ? " is-highlight" : ""
        }`}
      >
        <Box
          className={`message-bubble ${
            isOutgoing ? "outgoing" : "incoming"
          }`}
        >
          {replyTo && (
            <button
              type="button"
              className="message-reply"
              onClick={() => onScrollToReply(replyTo?._id)}
              aria-label="Jump to original message"
            >
              <span className="message-reply-name">
                Replying to {replyName}
              </span>
              <span className="message-reply-snippet">{replyPreview}</span>
            </button>
          )}
          {messageText && (
            <Typography variant="body2">{messageText}</Typography>
          )}
          {attachments.length > 0 && (
            <MessageAttachments
              attachments={attachments}
              onImagePreview={onImagePreview}
            />
          )}
          <Typography className="message-timestamp">
            {formatDateTimeLabel(messageTimestamp)}
          </Typography>
        </Box>
        <Box className="message-actions">
          <IconButton
            size="small"
            className="message-action-button"
            aria-label="Reply to message"
            onClick={() => onReply(message)}
          >
            <ReplyOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            className="message-action-button"
            aria-label="Add reaction"
            onClick={() => onToggleReactionPicker(messageId)}
          >
            <EmojiEmotionsOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <MessageReactions
          messageId={messageId}
          reactions={reactions}
          reactionPickerForId={reactionPickerForId}
          onToggleReaction={onToggleReaction}
          currentUserId={currentUserId}
          role={role}
        />
      </Box>
    </Fragment>
  );
};

export default MessageRow;
